// SolutionConfigFields — the ONE solution-config field body (ADR-056 §6), shared
// by every host (the peek's SolutionConfigForm drawer, the create wizard's
// Solution step) so the surfaces cannot drift. SOURCE-BLOCK layout: one card per
// source, its domain icon + a presence toggle in the header; switching a source
// on reveals its few fields inside the card (progressive disclosure). Off is an
// honest answer — the has-flags are the activation floor ("sources declared"),
// per-source VALUES are tracked as Missing, never a write gate (ADR-056 §4).
// Cooling is a site LOAD block (same card grammar); the rated system capacity is
// the always-visible field below the blocks. Discovered hints (open suggestions)
// ride each field with a one-click adopt.
//
// Pure fields — hosts own the surrounding meta (the drawer's date/reason block,
// the wizard's "since when"), the RHF form, and the submit.

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import type { TFunction } from 'i18next';

import { Button } from '../ui/Button.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { NumberInput } from '../ui/NumberInput.js';
import { icons, type Icon } from '../ui/icons.js';
import { Picker } from './Picker.js';
import { vocabularySource } from '../../hooks/useOptions.js';
import { BATTERY_CHEMISTRIES } from '@ems-app/schemas/sites/requests';
import { OBSERVABLE_FIELD_KIND, type DiscoverableConfigField } from '@ems-app/schemas/sites/discoveries';
import type { SiteSuggestion } from '@ems-app/schemas/sites/responses';
import {
  CONFIG_FIELD_META,
  FORM_SECTIONS,
  SECTION_LABEL_KEY,
  discoveredDisplay,
  type ConfigSection,
} from './solutionConfig.js';

/** The flat RHF value shape every host binds these fields into (wire keys). */
export type ConfigFormValues = Record<string, string | number | boolean | null>;

const SECTION_ICON: Record<Exclude<ConfigSection, 'system'>, Icon> = {
  grid: icons.sourceGrid,
  solar: icons.sourceSolar,
  generator: icons.sourceGenerator,
  providerGenerator: icons.sourceProviderGen,
  battery: icons.sourceBattery,
  wind: icons.sourceWind,
  cooling: icons.sourceCooling,
};

const FIELD_DECIMALS: Record<string, 0 | 1 | 2> = { kVA: 2, kW: 2, kWp: 2, A: 0, Ah: 0, L: 0 };

export interface SolutionConfigFieldsProps {
  /** The host's RHF form — the wire-keyed config fields live at its top level. */
  form: UseFormReturn<ConfigFormValues>;
  /** OPEN suggestions — each drifted field gets its discovered hint + one-click adopt. */
  suggestions?: SiteSuggestion[];
  t: TFunction;
}

export function SolutionConfigFields({ form, suggestions = [], t }: SolutionConfigFieldsProps) {
  const open = suggestions.filter((s) => s.status === 'open');
  const presence = form.watch();
  // The battery chemistry — a vocabulary on the family's picker (ADR-080 §3), its words memoised once per language.
  const chemistrySource = useMemo(() => vocabularySource('sites:battery_chemistry', BATTERY_CHEMISTRIES, (v) => t(`solution.chemistry.${v}`)), [t]);

  // Enabling a source moves focus to its first field once the card expands. The
  // enabling click records the target in a ref; the effect fires when the enabled
  // count changes (a card just opened) and focuses it — no setState-in-effect.
  const fieldContainers = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingFocus = useRef<ConfigSection | null>(null);
  const enabledCount = FORM_SECTIONS.filter((s) => s.flag && presence[s.flag]).length;
  useEffect(() => {
    const section = pendingFocus.current;
    if (!section) return;
    fieldContainers.current[section]?.querySelector<HTMLElement>('input, button, [tabindex]')?.focus();
    pendingFocus.current = null;
  }, [enabledCount]);

  const hintFor = (field: DiscoverableConfigField): ReactNode => {
    const s = open.find((x) => x.field === field);
    if (!s) return undefined;
    const kind = OBSERVABLE_FIELD_KIND[field];
    const adopt = () => {
      const raw = s.discovered;
      const value =
        kind === 'boolean' ? raw === 'true' : kind === 'integer' || kind === 'decimal' ? (raw === null ? null : Number(raw)) : raw;
      form.setValue(field, value as never, { shouldDirty: true });
    };
    return (
      <div className="mt-1 flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
        <span dir="auto">{t('reconcile.form.discovered', { value: discoveredDisplay(field, s.discovered, t) })}</span>
        <Button variant="link" onClick={adopt}>
          {t('reconcile.form.useDiscovered')}
        </Button>
      </div>
    );
  };

  const fieldRow = (field: DiscoverableConfigField): ReactNode => {
    const meta = CONFIG_FIELD_META[field];
    const kind = OBSERVABLE_FIELD_KIND[field];
    const label = meta.unit ? `${t(meta.labelKey)} (${meta.unit})` : t(meta.labelKey);
    if (kind === 'boolean') {
      return (
        <div key={field}>
          <Controller
            control={form.control}
            name={field}
            render={({ field: f }) => <Checkbox checked={Boolean(f.value)} onChange={f.onChange} label={label} />}
          />
          {hintFor(field)}
        </div>
      );
    }
    if (kind === 'integer' || kind === 'decimal') {
      return (
        <div key={field}>
          <Label htmlFor={`fld-${field}`}>{label}</Label>
          <Controller
            control={form.control}
            name={field}
            render={({ field: f }) => (
              <NumberInput
                id={`fld-${field}`}
                name={field}
                value={typeof f.value === 'number' ? f.value : null}
                onChange={f.onChange}
                decimals={FIELD_DECIMALS[meta.unit ?? ''] ?? 0}
              />
            )}
          />
          {hintFor(field)}
        </div>
      );
    }
    if (kind === 'battery_chemistry') {
      // The picker names its own trigger by the label; `name` lets a host that opens ON this field find it.
      return (
        <div key={field}>
          <Controller
            control={form.control}
            name={field}
            render={({ field: f }) => (
              <Picker source={chemistrySource} label={label} name={field} value={typeof f.value === 'string' ? f.value : null} onChange={f.onChange} clearable />
            )}
          />
          {hintFor(field)}
        </div>
      );
    }
    return (
      <div key={field}>
        <Label htmlFor={`fld-${field}`}>{label}</Label>
        <Controller
          control={form.control}
          name={field}
          render={({ field: f }) => (
            <Input
              id={`fld-${field}`}
              name={field}
              value={typeof f.value === 'string' ? f.value : ''}
              onChange={(e) => f.onChange(e.target.value || null)}
            />
          )}
        />
        {hintFor(field)}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {FORM_SECTIONS.filter((s) => s.flag !== null).map(({ section, flag, fields }) => {
        const on = Boolean(presence[flag!]);
        const BlockIcon = SECTION_ICON[section as Exclude<ConfigSection, 'system'>];
        return (
          <section
            key={section}
            className={`overflow-hidden rounded-lg border bg-[var(--color-bg-surface)] transition-colors ${
              on ? 'border-[var(--color-primary-display)]' : 'border-[var(--color-border)]'
            }`}
          >
            {/* Vertically centered header (icon + label) — the domain icon reads as the
                source's mark. OFF: the WHOLE card is one enable target (bigger than the
                box); a decorative unchecked box keeps the affordance. ON: the checkbox is
                the DISABLE control (only it toggles off — clicking the fields never does). */}
            <Controller
              control={form.control}
              name={flag!}
              render={({ field: f }) =>
                on ? (
                  <div className="p-3">
                    <Checkbox
                      checked
                      onChange={f.onChange}
                      label={
                        <span className="inline-flex items-center gap-2">
                          <BlockIcon size={20} aria-hidden className="text-[var(--color-primary-display)]" />
                          <span className="font-medium">{t(SECTION_LABEL_KEY[section])}</span>
                        </span>
                      }
                    />
                    {hintFor(flag!)}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      pendingFocus.current = section;
                      f.onChange(true);
                    }}
                    className="flex w-full items-center gap-2.5 p-3 text-start outline-none transition-colors hover:bg-[var(--color-bg-hover)] focus-ring-inset"
                  >
                    <span
                      aria-hidden
                      className="flex h-[1.15rem] w-[1.15rem] shrink-0 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-page)]"
                    />
                    <BlockIcon size={20} aria-hidden className="text-[var(--color-text-secondary)]" />
                    <span className="font-medium">{t(SECTION_LABEL_KEY[section])}</span>
                  </button>
                )
              }
            />
            {on && (
              <div
                ref={(el) => {
                  fieldContainers.current[section] = el;
                }}
                className="grid gap-3 border-t border-[var(--color-divider)] p-3 ps-10 sm:grid-cols-2"
              >
                {fields.map(fieldRow)}
              </div>
            )}
          </section>
        );
      })}
      <section className="pt-1">
        <h3 className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">{t(SECTION_LABEL_KEY.system)}</h3>
        <div className="grid gap-3 sm:grid-cols-2">{fieldRow('power_system_capacity_kw')}</div>
      </section>
    </div>
  );
}
